import { computed, inject } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'

import { lastValueFrom } from 'rxjs'

import {
  mapResource,
  mapResourcesList,
  withManifestAuditInfo,
} from '@flowx/designer/shared/entities/application.entity'

import {
  System,
  SystemSummary,
  SystemType,
  newSystemPayload,
  updateSystemPayload,
} from '../../models/system.entity'
import { SystemApiService } from '../../services/system.api'

type SystemsStore = {
  systems: SystemSummary[]
  query: string
  typeFilters: SystemType[]
  loading: boolean
  selectedSystemId: System['resourceId'] | null
  loaded: boolean
}
const initialState: SystemsStore = {
  systems: [],
  query: '',
  typeFilters: [],
  loading: false,
  loaded: false,
  selectedSystemId: null,
}

export const SystemStore = signalStore(
  withState(initialState),
  withComputed((store, route = inject(ActivatedRoute)) => ({
    filteredSystems: computed(() => {
      const query = store.query() || route.snapshot.queryParams.searchQuery
      const systems = store.systems()

      if (!query?.length) {
        return systems
      }
      return systems.filter((system) => system.name.toLowerCase().includes(query.toLowerCase()))
    }),
    selected: computed(() =>
      store.systems().find((s) => s.resourceId === store.selectedSystemId())
    ),
  })),
  withMethods((store, systemApiService = inject(SystemApiService)) => ({
    loadAll: async (filterUseInEnumerations: boolean = false) => {
      patchState(store, { loading: true })
      const types = store.typeFilters()
      const response = await lastValueFrom(
        systemApiService.getSystems(filterUseInEnumerations, types.length ? types : undefined)
      )
      const systems = withManifestAuditInfo(mapResourcesList(response), response.manifestInfo)
      patchState(store, {
        systems: systems.sort(
          (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        ),
        loading: false,
        loaded: true,
      })
    },
    filter: (query: string) => {
      patchState(store, { query })
    },
    setTypeFilters: (typeFilters: SystemType[]) => {
      patchState(store, { typeFilters })
    },
    create: async (system: newSystemPayload) => {
      const newSystem = await lastValueFrom(systemApiService.createSystem(system))
      patchState(store, { systems: [mapResource(newSystem), ...store.systems()] })
      return mapResource(newSystem)
    },
    update: async (system: updateSystemPayload) => {
      const existingSystem = store.systems().find((s) => s.resourceId === system.resourceId)
      const updatedSystem = mapResource(
        await lastValueFrom(systemApiService.updateSystem({ ...existingSystem, ...system }))
      )
      patchState(store, {
        systems: store.systems().map((s) => (s.id === updatedSystem.id ? updatedSystem : s)),
      })
      return updatedSystem
    },
    delete: async (systemResourceId: System['resourceId']) => {
      await lastValueFrom(systemApiService.deleteSystem(systemResourceId))
      patchState(store, {
        systems: store.systems().filter((s) => s.resourceId !== systemResourceId),
      })
    },
    setSelected: (systemId: System['id']) => {
      patchState(store, { selectedSystemId: systemId })
    },
    resetSelected: () => {
      patchState(store, { selectedSystemId: null })
    },
  }))
)
